import { useState, useEffect, useCallback, useRef } from 'react';
import { Tooltip as MuiTooltip, useTheme } from '@mui/material';
import type { TooltipProps as MuiTooltipProps } from '@mui/material';
import { colors } from 'styles/theme/colors';

// Global state to block all tooltips during scroll
let isScrolling = false;
let scrollTimer: ReturnType<typeof setTimeout> | undefined;

const handleScroll = () => {
  isScrolling = true;

  clearTimeout(scrollTimer);

  scrollTimer = setTimeout(() => {
    isScrolling = false;
  }, 150);
};

if (typeof document !== 'undefined') {
  document.addEventListener('wheel', handleScroll, { passive: true });
  document.addEventListener('scroll', handleScroll, {
    passive: true,
    capture: true,
  });
}

type PopperModifier = {
  name: string;
  options?: Record<string, unknown>;
};

const WRAP_TOOLTIP_POPPER_MODIFIERS: PopperModifier[] = [
  {
    name: 'flip',
    options: {
      fallbackPlacements: ['top', 'bottom', 'right', 'left'],
    },
  },
  {
    name: 'preventOverflow',
    options: {
      boundary: 'viewport',
      altAxis: true,
      padding: 8,
    },
  },
];

export type TooltipProps = Pick<
  MuiTooltipProps,
  | 'title'
  | 'sx'
  | 'children'
  | 'arrow'
  | 'disableInteractive'
  | 'disableFocusListener'
  | 'disableHoverListener'
  | 'followCursor'
  | 'id'
  | 'onClose'
  | 'onOpen'
  | 'open'
  | 'placement'
  | 'slots'
  | 'slotProps'
  | 'enterDelay'
  | 'enterNextDelay'
> & {
  /** Render with the error palette (red background + border, error-foreground text). */
  error?: boolean;
  /**
   * Opt-in for long free-text tooltips (e.g. table cell descriptions): wraps text,
   * caps width/height, anchors to the element (not the cursor), and keeps the popper
   * inside the viewport. Off by default so short label tooltips are unchanged.
   */
  wrapLongText?: boolean;
};

/**
 * Tooltip component, part of Delogue design system.
 * @param title - the content to display in the tooltip
 * @param children - the element that triggers the tooltip on hover
 * @param arrow - whether to display an arrow pointing to the trigger element, default is false
 * @param disableInteractive - if true, the tooltip won't be interactive and will close when the user hovers over it, default is false
 * @param placement - the placement of the tooltip relative to the trigger element
 * @param onOpen - callback function to be called when the tooltip opens
 * @param onClose - callback function to be called when the tooltip closes
 * @param slots - custom slot components to override default tooltip parts
 * @param slotProps - additional props to pass to the slot components
 */
export const Tooltip = ({
  sx,
  arrow = false,
  title,
  children,
  placement,
  disableInteractive = false,
  disableFocusListener = false,
  disableHoverListener = false,
  followCursor = true,
  onOpen,
  onClose,
  open: openProp,
  slotProps,
  slots,
  enterDelay,
  enterNextDelay,
  error = false,
  wrapLongText = false,
}: TooltipProps) => {
  const theme = useTheme();

  const [openState, setOpenState] = useState(false);
  // When we dismiss on scroll / pointer-leave we want the tooltip gone instantly
  // (no exit fade), so it can't appear to linger while the user keeps scrolling.
  // Ordinary hover-out keeps the normal fade.
  const [instantClose, setInstantClose] = useState(false);

  const triggerRef = useRef<Element | null>(null);
  const popperRef = useRef<Element | null>(null);
  const closedRef = useRef(false);

  const isControlled = openProp !== undefined;
  const open = isControlled ? openProp : openState;

  const tooltipBg = error ? theme.palette.bg.errorPrimary : colors.grey[600];

  const tooltipFg = error ? theme.palette.fg.errorPrimary : undefined;

  const setTriggerNode = useCallback((node: unknown) => {
    triggerRef.current = node instanceof Element ? node : null;
  }, []);

  const setPopperNode = useCallback((node: unknown) => {
    popperRef.current = node instanceof Element ? node : null;
  }, []);

  const closeTooltip = useCallback(
    (event: React.SyntheticEvent | Event) => {
      if (!isControlled) {
        if (closedRef.current) return;

        closedRef.current = true;
        setOpenState(false);
      }

      onClose?.(event);
    },
    [isControlled, onClose]
  );

  useEffect(() => {
    if (!open) return;

    const close = () => {
      // Scroll / pointer-leave dismissals hide with no exit transition.
      if (!isControlled) setInstantClose(true);
      closeTooltip(new Event('close'));
    };
    const captureOpts: AddEventListenerOptions = { capture: true };

    // Dismiss on actual scrolling (required for the virtualized table: a scroll
    // recycles the anchor row without moving the cursor, so no pointermove fires
    // and the tooltip would otherwise stay stuck — DL-61504). Listen for `scroll`,
    // NOT `wheel`: a wheel spin on a non-scrollable page fires `wheel` but nothing
    // moves, and closing then would hide the tooltip for no reason. `scroll` only
    // fires when content actually moves; capture catches scroll from any container.
    document.addEventListener('scroll', close, { capture: true, passive: true });

    // Pointer-off net: dismiss as soon as the cursor leaves both the trigger and
    // the tooltip popper. Only for uncontrolled tooltips — controlled ones are
    // parent-driven, so scroll dismissal alone applies to them.
    const handlePointerMove = (event: PointerEvent) => {
      const target = event.target;
      const inside =
        target instanceof Node &&
        (Boolean(triggerRef.current?.contains(target)) ||
          Boolean(popperRef.current?.contains(target)));

      if (!inside) close();
    };

    if (!isControlled) {
      document.addEventListener('pointermove', handlePointerMove, { passive: true });
    }

    return () => {
      document.removeEventListener('scroll', close, captureOpts);
      document.removeEventListener('pointermove', handlePointerMove);
    };
  }, [open, isControlled, closeTooltip]);

  useEffect(() => {
    if (!isControlled && !title && open) {
      setOpenState(false);
    }
  }, [title, open, isControlled]);

  const handleOpen = useCallback(
    (event: React.SyntheticEvent) => {
      if (isScrolling) return;

      closedRef.current = false;
      setInstantClose(false); // restore the normal fade for the next hover-out

      if (!isControlled) {
        setOpenState(true);
      }

      onOpen?.(event);
    },
    [isControlled, onOpen]
  );

  return (
    <MuiTooltip
      ref={setTriggerNode}
      title={title}
      sx={{
        marginTop: 0,
        ...sx,
      }}
      arrow={arrow}
      open={open}
      onOpen={handleOpen}
      onClose={closeTooltip}
      placement={placement}
      followCursor={!wrapLongText && followCursor}
      disableInteractive={disableInteractive}
      disableFocusListener={disableFocusListener}
      disableHoverListener={disableHoverListener}
      slots={slots}
      enterDelay={enterDelay}
      enterNextDelay={enterNextDelay}
      leaveDelay={0}
      slotProps={{
        arrow: {
          sx: {
            color: tooltipBg,
          },
        },

        tooltip: {
          sx: {
            background: tooltipBg,
            ...(tooltipFg && {
              color: tooltipFg,
            }),
            fontSize: error ? '0.75rem' : theme.typography.smallTitle.fontSize,
            lineHeight: error ? 1.4 : theme.typography.smallTitle.lineHeight,
            paddingX: theme.spacing(3),
            paddingY: theme.spacing(2),
            transition: 'none !important',
            ...(wrapLongText && {
              whiteSpace: 'normal',
              wordBreak: 'break-word',
              maxWidth: 360,
              maxHeight: '60vh',
              overflow: 'hidden',
            }),
          },
        },

        transition: {
          timeout: {
            enter: 200,
            exit: instantClose ? 0 : 50,
          },
        },
        ...slotProps,
        popper: {
          ...slotProps?.popper,
          // Our tracking ref feeds the scroll / pointer-leave dismissal (DL-61504), so
          // it must win over any caller-supplied popper ref — spread caller props first.
          ref: setPopperNode,
          ...(wrapLongText && {
            popperOptions: {
              modifiers: WRAP_TOOLTIP_POPPER_MODIFIERS,
            },
          }),
          sx: {
            transition: 'none !important',
            ...(error && {
              '&[data-popper-placement*="bottom"] .MuiTooltip-tooltip': {
                marginTop: '5px',
              },
              '&[data-popper-placement*="top"] .MuiTooltip-tooltip': {
                marginBottom: '1px',
              },
              '&[data-popper-placement*="left"] .MuiTooltip-tooltip': {
                marginRight: '1px',
              },
              '&[data-popper-placement*="right"] .MuiTooltip-tooltip': {
                marginLeft: '1px',
              },
            }),
          },
        },
      }}
    >
      {children}
    </MuiTooltip>
  );
};
